/**
 * TikTok Shop Live - High-Performance Scheduler Engine
 * Sarelly Sarelly Store (CDMX Timezone: America/Mexico_City)
 */

(() => {
  'use strict';

  const TIMEZONE = 'America/Mexico_City';
  const DAY_MAP = Object.freeze({
    '0': 0, 'domingo': 0, 'sun': 0, 'sunday': 0,
    '1': 1, 'lunes': 1, 'mon': 1, 'monday': 1,
    '2': 2, 'martes': 2, 'tue': 2, 'tuesday': 2,
    '3': 3, 'miercoles': 3, 'miércoles': 3, 'wed': 3, 'wednesday': 3,
    '4': 4, 'jueves': 4, 'thu': 4, 'thursday': 4,
    '5': 5, 'viernes': 5, 'fri': 5, 'friday': 5,
    '6': 6, 'sabado': 6, 'sábado': 6, 'sat': 6, 'saturday': 6
  });

  // Cached Intl Formatter for maximum performance and zero garbage collection
  const cdmxFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TIMEZONE,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false
  });

  let timerId = null;

  function getCDMXNow() {
    try {
      const parts = cdmxFormatter.formatToParts(new Date());
      const p = {};
      for (let i = 0; i < parts.length; i++) {
        p[parts[i].type] = parts[i].value;
      }
      return new Date(p.year, p.month - 1, p.day, p.hour === '24' ? 0 : p.hour, p.minute, p.second);
    } catch {
      return new Date();
    }
  }

  function parseDateTime(str) {
    if (!str) return null;
    const cleanStr = str.trim().replace('T', ' ');
    const parts = cleanStr.split(' ');
    if (parts.length < 2) return null;

    const dateParts = parts[0].split(/[-/]/);
    const timeParts = parts[1].split(':');

    if (dateParts.length < 3 || timeParts.length < 2) return null;

    const year = Number.parseInt(dateParts[0], 10);
    const month = Number.parseInt(dateParts[1], 10) - 1;
    const day = Number.parseInt(dateParts[2], 10);
    const hours = Number.parseInt(timeParts[0], 10);
    const minutes = Number.parseInt(timeParts[1], 10);

    if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    return new Date(year, month, day, hours, minutes, 0);
  }

  function parseTimeOnDate(timeStr, baseDate) {
    if (!timeStr) return null;
    const parts = timeStr.trim().split(':');
    if (parts.length < 2) return null;

    const hours = Number.parseInt(parts[0], 10);
    const minutes = Number.parseInt(parts[1], 10);

    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

    return new Date(
      baseDate.getFullYear(),
      baseDate.getMonth(),
      baseDate.getDate(),
      hours,
      minutes,
      0
    );
  }

  function evaluateElement(el, nowCDMX) {
    const mode = el.getAttribute('data-status-mode') || 'schedule';
    const isDesignMode = Boolean(window.Shopify?.designMode);
    const forcePreview = el.getAttribute('data-force-preview') === 'true';

    // Theme Editor forced preview
    if (isDesignMode && forcePreview) {
      return { isLive: true, nextTransition: null };
    }

    if (mode === 'always') {
      return { isLive: true, nextTransition: null };
    }

    if (mode === 'off') {
      return { isLive: false, nextTransition: null };
    }

    // Schedule Mode
    let isLive = false;
    let nextTransition = null;

    // 1. Specific Date/Time Range
    const startStr = el.getAttribute('data-start-datetime');
    const endStr = el.getAttribute('data-end-datetime');

    if (startStr && endStr) {
      const startDate = parseDateTime(startStr);
      const endDate = parseDateTime(endStr);

      if (startDate && endDate) {
        if (nowCDMX >= startDate && nowCDMX <= endDate) {
          isLive = true;
          nextTransition = endDate;
        } else if (nowCDMX < startDate) {
          nextTransition = startDate;
        }
      }
    }

    // 2. Weekly Recurring Schedule
    const recurring = el.getAttribute('data-recurring-enabled') === 'true';
    if (recurring) {
      const daysStr = el.getAttribute('data-recurring-days') || '';
      const recStartTime = el.getAttribute('data-recurring-start-time');
      const recEndTime = el.getAttribute('data-recurring-end-time');

      if (daysStr && recStartTime && recEndTime) {
        const currentDay = nowCDMX.getDay();
        const allowedDays = daysStr.toLowerCase().split(',').map(d => d.trim());
        const isTodayAllowed = allowedDays.some(d => DAY_MAP[d] === currentDay);

        const recStart = parseTimeOnDate(recStartTime, nowCDMX);
        const recEnd = parseTimeOnDate(recEndTime, nowCDMX);

        if (isTodayAllowed && recStart && recEnd) {
          if (nowCDMX >= recStart && nowCDMX <= recEnd) {
            isLive = true;
            if (!nextTransition || recEnd < nextTransition) {
              nextTransition = recEnd;
            }
          } else if (nowCDMX < recStart) {
            if (!nextTransition || recStart < nextTransition) {
              nextTransition = recStart;
            }
          }
        }
      }
    }

    return { isLive, nextTransition };
  }

  function updateTikTokLive() {
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }

    const elements = document.querySelectorAll('.tiktok-live-element');
    if (!elements.length) return;

    const nowCDMX = getCDMXNow();
    let minMsUntilNext = null;

    // Detect if an active, non-dismissed floating widget is currently on the page
    const floatingWidgets = document.querySelectorAll('.tiktok-live-floating-widget');
    let hasVisibleFloatingWidget = false;

    floatingWidgets.forEach(fw => {
      const fwId = fw.getAttribute('data-element-id') || 'default';
      const isDismissed = sessionStorage.getItem(`sarelly_tiktok_live_dismissed_${fwId}`) === 'true';
      const { isLive } = evaluateElement(fw, nowCDMX);
      if (isLive && !isDismissed) {
        hasVisibleFloatingWidget = true;
      }
    });

    elements.forEach(el => {
      const elementId = el.getAttribute('data-element-id') || 'default';
      const isFloating = el.classList.contains('tiktok-live-floating-widget');
      const isHeaderBadge = el.classList.contains('tiktok-live-header-badge');
      const isDismissed = sessionStorage.getItem(`sarelly_tiktok_live_dismissed_${elementId}`) === 'true';

      // If floating widget was dismissed for current session, hide it
      if (isFloating && isDismissed) {
        el.setAttribute('data-tiktok-live-hidden', 'true');
        return;
      }

      const { isLive, nextTransition } = evaluateElement(el, nowCDMX);

      if (isLive) {
        const onlyAfterFloating = el.getAttribute('data-show-after-floating-dismissed') !== 'false';

        // If it's a header badge and an active floating widget is currently on screen, keep header badge hidden until floating is dismissed
        if (isHeaderBadge && hasVisibleFloatingWidget && onlyAfterFloating) {
          el.setAttribute('data-tiktok-live-hidden', 'true');
        } else {
          el.removeAttribute('data-tiktok-live-hidden');
        }
      } else {
        el.setAttribute('data-tiktok-live-hidden', 'true');
      }

      if (nextTransition) {
        const ms = nextTransition.getTime() - nowCDMX.getTime();
        if (ms > 0 && (minMsUntilNext === null || ms < minMsUntilNext)) {
          minMsUntilNext = ms;
        }
      }
    });

    // Smart Scheduling: schedule exact timer for the next transition (+ 500ms margin), capped between 1s and 1 hour
    if (minMsUntilNext !== null && minMsUntilNext > 0) {
      const delay = Math.min(Math.max(minMsUntilNext + 500, 1000), 3600000);
      timerId = setTimeout(updateTikTokLive, delay);
    }
  }

  // Event delegation for close buttons (zero memory leaks, reveals header badge on dismiss)
  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-tiktok-live-close]');
    if (!closeBtn) return;

    e.preventDefault();
    e.stopPropagation();

    const el = closeBtn.closest('.tiktok-live-element');
    if (!el) return;

    const elementId = el.getAttribute('data-element-id') || 'default';
    el.setAttribute('data-tiktok-live-hidden', 'true');
    sessionStorage.setItem(`sarelly_tiktok_live_dismissed_${elementId}`, 'true');

    // Immediately evaluate so the header badge appears smoothly
    updateTikTokLive();
  });

  // Re-evaluate when tab becomes visible again (handles background sleep / waking up)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      updateTikTokLive();
    }
  });

  // Lifecycle Initialization
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', updateTikTokLive, { once: true });
  } else {
    updateTikTokLive();
  }

  // Shopify Theme Editor Integration
  document.addEventListener('shopify:section:load', updateTikTokLive);
  document.addEventListener('shopify:section:select', updateTikTokLive);
  document.addEventListener('shopify:block:select', updateTikTokLive);

  window.initTikTokLiveElements = updateTikTokLive;
})();
